/* global Office, window */

import { API_CONFIG } from "../config/api.js";
import {
  clearWebSession,
  getLegacyApiKey,
  setAuthConfig,
  setWebSession,
} from "./session.js";
import {
  completeWebLoginAttempt,
  createWebLoginAttempt,
  parseAuthResponse,
} from "./webAttempt.js";
const AUTH_CONFIG_RETRY_DELAYS_MS = [0, 500, 1000, 2000, 4000, 8000];
const TRANSIENT_AUTH_STATUSES = new Set([502, 503, 504]);

function wait(milliseconds) {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

export async function loadAuthConfig({ retryDelaysMs = AUTH_CONFIG_RETRY_DELAYS_MS } = {}) {
  let lastError = null;

  for (let attempt = 0; attempt < retryDelaysMs.length; attempt += 1) {
    if (retryDelaysMs[attempt] > 0) {
      await wait(retryDelaysMs[attempt]);
    }

    try {
      const response = await fetch(`${API_CONFIG.baseURL}/auth/config`, {
        credentials: "include",
        cache: "no-store",
      });
      const canRetry =
        TRANSIENT_AUTH_STATUSES.has(response.status) &&
        attempt < retryDelaysMs.length - 1;
      if (canRetry) continue;

      const value = await parseAuthResponse(response);
      setAuthConfig(value);
      return value;
    } catch (error) {
      lastError = error;
      const canRetry =
        typeof error?.status !== "number" &&
        attempt < retryDelaysMs.length - 1;
      if (!canRetry) throw error;
    }
  }

  throw lastError || new Error("Cannot reach the authentication service");
}

export async function recoverWebSession() {
  const response = await fetch(`${API_CONFIG.baseURL}/auth/session`, {
    credentials: "include",
    cache: "no-store",
  });
  if (response.status === 401 || response.status === 403) {
    clearWebSession();
    return null;
  }
  const session = await parseAuthResponse(response);
  setWebSession(session);
  return session;
}

export async function validateLegacyApiKey(apiKey) {
  const response = await fetch(`${API_CONFIG.baseURL}/auth/me`, {
    headers: { "X-Api-Key": apiKey },
    credentials: "omit",
    cache: "no-store",
  });
  return parseAuthResponse(response);
}

function openOfficeDialog(url) {
  return new Promise((resolve, reject) => {
    Office.context.ui.displayDialogAsync(
      url,
      { height: 65, width: 40, displayInIframe: false },
      (result) => {
        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          reject(new Error(result.error?.message || "Could not open sign-in"));
          return;
        }
        const dialog = result.value;
        const close = () => {
          try { dialog.close(); } catch (_) { /* already closed */ }
        };
        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (event) => {
          try {
            if (event.origin && event.origin !== window.location.origin) {
              throw new Error("Sign-in dialog origin did not match the taskpane");
            }
            const message = JSON.parse(event.message || "{}");
            if (message.type === "novatime-auth-verified") {
              close();
              resolve(message);
            } else if (message.type === "novatime-auth-error") {
              close();
              reject(new Error(message.message || "Sign-in failed"));
            }
          } catch (error) {
            close();
            reject(error);
          }
        });
        dialog.addEventHandler(Office.EventType.DialogEventReceived, (event) => {
          close();
          reject(new Error(`Sign-in dialog closed (${event.error || "unknown"})`));
        });
      },
    );
  });
}

export async function signInWithOfficeDialog(intent = "signin") {
  let apiKey = null;
  if (intent === "link") {
    apiKey = getLegacyApiKey();
    if (!apiKey) throw new Error("Enter and validate the legacy API key before linking");
  }
  const attempt = await createWebLoginAttempt({ intent, apiKey });
  const dialogUrl = new URL("auth-dialog.html", window.location.href);
  dialogUrl.searchParams.set("attempt_id", attempt.attemptId);
  dialogUrl.searchParams.set("state", attempt.state);
  await openOfficeDialog(dialogUrl.toString());

  const session = await completeWebLoginAttempt({
    attemptId: attempt.attemptId,
    verifier: attempt.verifier,
  });
  setWebSession(session);
  return session;
}
